import { AxiosResponse } from "axios";
import BaseAPI from "../api";

/**
 * ตรงตาม spec ใน src/pages/depositRefund/deposit-refund-api.md (อัปเดต 2026-09-08)
 * ชื่อ field ทั้งไฟล์ยึดตามที่ backend ส่งมาเป๊ะ ไม่ตั้งชื่อใหม่ให้สวยกว่า
 * เพราะจะกลายเป็นชั้นแปลงชื่อที่ต้องมาไล่หาว่า field ไหนคือ field ไหน
 */

/** ฝั่งเจ้าหน้าที่กับฝั่งลูกค้าใช้ base path คนละเส้น แต่ shape เหมือนกันเกือบทั้งหมด */
export type DepositRefundSide = "ADMIN" | "CUSTOMER";

const BASE_PATH: Record<DepositRefundSide, string> = {
  ADMIN: "deposit-refund",
  CUSTOMER: "deposit-refund-request",
};

/** envelope มาตรฐาน — ฝั่งหน้าจออ่าน res.data.data ไม่ใช่ res.data */
export type BaseResponse<T> = {
  success: boolean;
  status: number;
  message: string;
  data: T;
};

/** nestjs-typeorm-paginate — total อยู่ใน meta ไม่ใช่ header 'total' แบบ API เก่า */
export type Paginated<T> = {
  items: T[];
  meta: {
    totalItems: number;
    itemCount: number;
    itemsPerPage: number;
    totalPages: number;
    currentPage: number;
  };
  links: { first: string; previous: string; next: string; last: string };
};

export type DepositRefundStatus = "WAITING" | "APPROVE" | "NOT_APPROVE" | "CANCELED";

/** DELETED ไม่เคยคืนมาใน API (soft delete) จึงไม่อยู่ใน union ข้างบน */
export type DepositRefundMethod = "BANK_TRANSFER" | "CASH" | "CHEQUE";

export type DepositRefundLogAction = "CREATE" | "REVIEW" | "CANCEL" | "DELETE";

export type DepositRefundDeductType = "MOVE_OUT_PENALTY" | "DAMAGE_INSURANCE";

export type LeaseStatus = "ACTIVE" | "INACTIVE" | "CANCEL" | "TRANSFER";

/**
 * ยอดเงินที่ backend คืนเป็น DECIMAL ของ MySQL → มาเป็น string
 * ยกเว้น response ของ POST /:id/review ที่คืนเป็น number
 * ทุกจุดที่ใช้ต้อง Number() ก่อนเสมอ (ดู utils/deposit-refund.utils.ts → toAmount)
 */
export type DecimalString = string | number;

export interface DepositRefundParams {
  keyword?: string;
  status?: DepositRefundStatus;
  /**
   * สาขาที่เลือกไว้ (getBranch()) — ฝั่งเจ้าหน้าที่ "ต้องส่งทุกเส้น"
   * ไม่ส่งจะเห็นคำร้องข้ามสาขา เพราะ backend ไม่ได้ scope จาก token (spec ข้อ 1.4)
   * ฝั่งลูกค้าไม่ต้องส่ง scope ด้วย userId จาก token อยู่แล้ว
   */
  branchId?: number;
  zoneId?: number;
  stallId?: number;
  /** YYYY-MM-DD — กรองที่วันที่ยื่นคำร้อง */
  date_from?: string;
  date_to?: string;
  page?: number;
  limit?: number;
}

/** แถวในตารางรายการคำร้อง (doc 3.1) */
export interface DepositRefundRow {
  id_deposit_refund: number;
  /** 'DEP-2569-0001' */
  deposit_refund_no: string;
  leaseAgreementId: number;
  lease_agreement_no: string | null;
  userId: number;
  customerId: number;
  tenant_name: string | null;
  zoneId: number | null;
  zone_name: string | null;
  stallId: number | null;
  stall_name: string | null;
  stall_number: string | null;
  stall_type_name: string | null;
  /** string (DECIMAL) */
  request_amount: DecimalString;
  /** null = ยังไม่พิจารณา หรือไม่อนุมัติ */
  approved_amount: DecimalString | null;
  refund_method: DepositRefundMethod | null;
  /** backend คืนภาษาไทยมาให้แล้ว ไม่ต้อง map เอง */
  refund_method_name: string | null;
  request_date: string | null;
  reviewed_date: string | null;
  reviewed_id: number | null;
  reviewed_name: string | null;
  status: DepositRefundStatus;
}

export interface DepositRefundSummary {
  all: number;
  WAITING: number;
  APPROVE: number;
  NOT_APPROVE: number;
  CANCELED: number;
}

/** option ของ dropdown โซน — เอามาจากคำร้องที่มีอยู่จริง ไม่ใช่ master ทั้งระบบ */
export interface DepositRefundZoneOption {
  zoneId: number;
  zone_name: string | null;
}

/**
 * option ของ dropdown แผงค้า
 * zoneId เป็นตัวเดียวกับที่ใช้กรอง ?zoneId= ในเส้น list จึงทำ cascading ฝั่ง client ได้
 * (กรอง option แผงค้าตามโซนที่เลือก) ไม่มีเคสที่กรองแล้วตารางว่างแบบหาสาเหตุไม่เจอ
 */
export interface DepositRefundStallOption {
  stallId: number;
  stall_name: string | null;
  stall_number: string | null;
  zoneId: number | null;
}

/** ผลค้นหาผู้ขอคืนค่ามัดจำ (doc 3.4) — ใช้ฝั่งเจ้าหน้าที่เท่านั้น */
export interface DepositRefundOwner {
  /** ใช้เรียก ownerLease() และส่งเป็น userId_owner ตอนสร้างคำร้อง */
  userId: number;
  customerId: number;
  owner_fullname: string;
  /** backend mask มาแล้ว ('577xxxxxxx720') ห้าม mask ซ้ำ */
  idCard: string | null;
  mobilePhone: string | null;
  /** string (COUNT ของ MySQL) */
  lease_count: DecimalString;
}

/** สัญญาเช่าที่มีเงินมัดจำ (doc 3.5) */
export interface DepositRefundLease {
  leaseAgreementId: number;
  branchId: number | null;
  userId: number;
  lease_agreement_no: string | null;
  tenant_name: string | null;
  /**
   * สัญญาที่ยกเลิก/โอนสิทธิแล้ว backend ไล่แผงจาก appointment -> bidder ให้
   * หาไม่เจอจริงๆ จะเป็น null ทั้ง stall/zone แต่แถวยังยื่นคำร้องได้ปกติ
   */
  stallId: number | null;
  zoneId: number | null;
  zone_name: string | null;
  stall_name: string | null;
  stall_number: string | null;
  stall_type_name: string | null;
  /** DATE ที่ serialize เป็น UTC ISO ต้อง format ด้วย timezone Asia/Bangkok */
  date_start: string | null;
  date_end: string | null;
  lease_status: LeaseStatus;
  /** backend คืนคำไทยมาให้แล้ว ใช้แสดงตรงๆ ไม่ต้อง map */
  lease_status_name: string | null;
  move_out_penalty_rate: number;
  damage_insurance_limit: number;
  /** number ตรงๆ (ต่างจาก request_amount ที่เป็น string) */
  deposit_amount: number;
  /** เลขที่คำร้องที่กำลังยึดสิทธิสัญญานี้อยู่ */
  blocked_by_no: string | null;
  /**
   * สถานะของใบที่ยึดสิทธิ — WAITING (ยกเลิกใบเดิมแล้วยื่นใหม่ได้) vs APPROVE (จบแล้ว)
   * ความหมายต่างกันมากกับผู้ใช้ จึงแยกไว้เผื่อทำสี/ปุ่มต่างกัน
   */
  blocked_by_status: DepositRefundStatus | null;
  /** ข้อความไทยพร้อมใช้ ใส่ tooltip ได้ตรงๆ (null เมื่อ can_request = true) */
  blocked_reason: string | null;
  /** false = ยื่นคำร้องสัญญานี้ไม่ได้ ต้อง disable แถว */
  can_request: boolean;
}

/** 1 บรรทัดใน "รายละเอียดการคำนวณเงินคืน" */
export interface DepositRefundDeduct {
  id_deposit_refund_deduct?: number;
  seq?: number;
  deduct_type: DepositRefundDeductType;
  /** snapshot ตอนพิจารณา เอาไปโชว์ตรงๆ ได้ */
  label: string;
  base_amount: DecimalString;
  deduct_amount: DecimalString;
  remain_amount: DecimalString;
}

/**
 * ยอดหักตั้งต้นที่ดึงจากคำสั่ง "เรียกคืนแผงค้า" ของสัญญาใบเดียวกัน
 * (deposit-refund-deduct-default-api.md)
 *
 * ยอดเงินทุกช่องในเส้นนี้เป็น number แล้ว (backend round2 มาให้) ต่างจากเส้นอื่น
 * ในโมดูลนี้ที่ยิง SUM() ตรงๆ แล้วได้ DECIMAL เป็น string — จึงไม่ใช้ DecimalString
 */
export interface DepositRefundDeductDefaultLine {
  seq: number;
  deduct_type: DepositRefundDeductType;
  label: string;
  /** เพดานของคำร้องใบนี้ = กรอกได้สูงสุดเท่านี้ */
  base_amount: number;
  /** ค่าที่แนะนำให้ prefill — clamp ด้วย base_amount มาแล้ว */
  deduct_amount: number;
  remain_amount: number;
  /** ยอดดิบจากคำสั่งเรียกคืนก่อน clamp — เทียบกับ deduct_amount เพื่อรู้ว่าโดนตัดไหม */
  source_deduct_amount: number;
}

/** คำสั่งเรียกคืนแผงค้าที่เป็นที่มาของยอดหัก */
export interface DepositRefundDeductDefaultSource {
  id_deposit_refund: number;
  /** เลขที่คำสั่ง — เจ้าหน้าที่กรอกเองได้ จึงไม่จำเป็นต้องขึ้นต้น RCL- */
  deposit_refund_no: string;
  request_type: "STALL_RECLAIM";
  status: "APPROVE";
  due_date: string | null;
  reviewed_date: string | null;
  reviewed_name: string | null;
  review_note: string | null;
  approved_amount: number;
}

export interface DepositRefundDeductDefault {
  id_deposit_refund: number;
  deposit_refund_no: string;
  leaseAgreementId: number;
  lease_agreement_no: string | null;
  /** สถานะของคำร้องใบนี้ ไม่ใช่ของคำสั่งเรียกคืน */
  status: DepositRefundStatus;
  /**
   * ตัวตัดสินเดียวว่ามีค่าให้ prefill หรือไม่
   * ห้ามใช้ `source !== null` แทน — มีเคสที่คำสั่งอนุมัติแล้วแต่เจ้าหน้าที่ไม่ได้กรอกยอดหัก
   * จะได้ source ครบแต่ has_default = false ซึ่งไม่มีอะไรให้เติม
   */
  has_default: boolean;
  deduct_move_out: number;
  deduct_damage: number;
  deduct_total: number;
  approved_amount: number;
  /** 2 บรรทัดเสมอ เรียงตาม seq */
  deducts: DepositRefundDeductDefaultLine[];
  source: DepositRefundDeductDefaultSource | null;
}

/** รายละเอียดคำร้อง (doc 3.6) */
export interface DepositRefundDetail {
  id_deposit_refund: number;
  deposit_refund_no: string;
  branchId: number | null;
  leaseAgreementId: number;
  lease_agreement_no: string | null;
  userId: number;
  customerId: number;
  tenant_name: string | null;
  tenant_idCard: string | null;
  tenant_mobilePhone: string | null;
  zoneId: number | null;
  zone_name: string | null;
  stallId: number | null;
  stall_name: string | null;
  stall_number: string | null;
  stall_type_name: string | null;
  /** DATE serialize เป็น UTC ISO */
  lease_date_end: string | null;
  lease_status: LeaseStatus | null;
  lease_status_name: string | null;
  move_out_penalty_rate: number;
  damage_insurance_limit: number;
  request_amount: DecimalString;
  refund_method: DepositRefundMethod | null;
  refund_method_name: string | null;
  request_note: string | null;
  request_date: string | null;
  /** บอกว่าลูกค้ายื่นเอง หรือเจ้าหน้าที่ยื่นแทน */
  request_by_id: number | null;
  request_by_name: string | null;
  reviewed_date: string | null;
  reviewed_id: number | null;
  reviewed_name: string | null;
  review_note: string | null;
  approved_amount: DecimalString | null;
  /** DATE serialize เป็น UTC ISO (ต่างจากใน log ที่คืน 'YYYY-MM-DD') */
  refund_date: string | null;
  doc_ref: string | null;
  status: DepositRefundStatus;
  /** [] ถ้ายังไม่พิจารณา หรือไม่อนุมัติ */
  deducts: DepositRefundDeduct[];
}

export interface DepositRefundFile {
  id_deposit_refund_file: number;
  /** '1788864174238-ใบตรวจรับคืนพื้นที่.pdf' — timestamp นำหน้ากันชื่อชนกัน */
  file_name: string;
  mimetype: string | null;
  /** string (BIGINT) หน่วย byte */
  file_size: DecimalString | null;
  /**
   * '/deposit-refund/1/file/1/download' — ต่อกับ baseURL ได้เลย
   * API จงใจไม่คืน file_url แบบ static path ตามข้อกำหนด PDPA
   */
  download_url: string;
}

/**
 * ที่มาของใบเสร็จ — ระบบออกใบเสร็จจาก 2 ที่ เส้นนี้รวมมาให้แล้วในตารางเดียว
 * INVOICE = ใบแจ้งหนี้ที่ชำระแล้ว · DEPOSIT = ใบคำร้องขอชำระเงินมัดจำ
 */
export type DepositRefundReceiptSource = "INVOICE" | "DEPOSIT";

/**
 * ตัวกรองของเส้นใบเสร็จ (deposit-refund-receipt-api.md ข้อ 1)
 * ชุดเดียวกับตัวกรองหัวหน้าจอ /pay-bill — ส่งเฉพาะตัวที่ต้องใช้ก็ได้
 */
export interface DepositRefundReceiptParams {
  /** ควรส่งทุกครั้ง — เส้นนี้ไม่ scope สาขาจาก token เหมือนเส้นอื่นในโมดูลนี้ */
  branchId?: number;
  /** เลขที่ใบแจ้งหนี้ / เลขที่คำร้อง / เลขที่ใบเสร็จ / ชื่อผู้เช่า / ชื่อ-เลขที่แผงค้า */
  keyword?: string;
  zoneId?: number;
  stallId?: number;
  /** เฉพาะใบเสร็จของผู้เช่ารายนี้ (users.userId) */
  userId?: number;
  /** ไม่ส่ง = รวมทั้ง 2 ที่มา */
  source?: DepositRefundReceiptSource;
  /** YYYY-MM-DD — กรองที่วันที่เอกสาร */
  date_from?: string;
  date_to?: string;
  page?: number;
  /** default 10 · backend clamp เพดานไว้ที่ 100 ส่งเกินไปก็ได้แค่ 100 */
  limit?: number;
}

/** 1 แถวในตารางใบเสร็จ — backend เรียง document_date DESC, document_no DESC มาให้แล้ว */
export interface DepositRefundReceiptRow {
  source: DepositRefundReceiptSource;
  /**
   * invoiceId หรือ depositId แล้วแต่ source — เลขซ้ำกันข้าม source ได้
   * ทุกที่ที่ใช้เป็น key หรือส่งต่อ ต้องใช้คู่กับ source เสมอ ไม่งั้นชี้ผิดเอกสาร
   */
  refId: number;
  /** เลขที่ใบแจ้งหนี้ (INVOICE) หรือเลขที่คำร้อง (DEPOSIT) */
  document_no: string | null;
  /** null ทั้งหมดในฝั่ง DEPOSIT — ระบบไม่ออกเลขใบเสร็จให้เงินมัดจำ */
  receipt_no: string | null;
  tenant_name: string | null;
  zoneId: number | null;
  zone_name: string | null;
  stallId: number | null;
  stall_name: string | null;
  stall_number: string | null;
  /** UTC ISO — format ด้วย timezone Asia/Bangkok เหมือน field DATE อื่นในโมดูลนี้ */
  document_date: string | null;
  /** สถานะของเอกสารต้นทาง (PAY_APPROVE / SUCCESS / APPOINTMENT / SUCCESS_AGREEMENT) */
  status: string | null;
  branchId: number | null;
  /** 'ใบแจ้งหนี้' / 'ใบคำร้องขอชำระเงินมัดจำ' — backend คืนคำไทยมาให้แล้ว ไม่ต้อง map */
  source_name: string | null;
  /** relative path ไม่มี / นำหน้า ('invoice/11266/receipt') ต่อกับ baseURL ของ axios ได้เลย */
  download_url: string;
}

/** ประวัติ (doc 3.9) เรียงใหม่ -> เก่ามาแล้ว */
export interface DepositRefundLog {
  id_deposit_refund_log: number;
  id_deposit_refund: number;
  action: DepositRefundLogAction;
  /** null เมื่อ action ไม่ใช่ REVIEW */
  result: "APPROVE" | "NOT_APPROVE" | null;
  note: string | null;
  approved_amount?: DecimalString | null;
  /** ใน log คืนเป็น 'YYYY-MM-DD' */
  refund_date?: string | null;
  doc_ref?: string | null;
  action_id: number | null;
  action_name: string | null;
  action_date: string | null;
  /** มีค่าเฉพาะ log ที่ action = REVIEW และ result = APPROVE */
  deducts: DepositRefundDeduct[];
  files: DepositRefundFile[];
}

/** body ของ POST /deposit-refund — เจ้าหน้าที่ยื่นแทนลูกค้า ได้หลายสัญญาพร้อมกัน */
export interface CreateDepositRefundBody {
  userId_owner: number;
  leaseAgreementIds: number[];
  refund_method?: DepositRefundMethod;
  note?: string;
}

export interface CreateDepositRefundItem {
  id_deposit_refund: number;
  deposit_refund_no: string;
  leaseAgreementId: number;
  lease_agreement_no: string | null;
  request_amount: DecimalString;
}

/** 201 ถึงจะมีบางใบถูกข้าม ต้องอ่าน skipped[] มาแจ้งผู้ใช้ด้วย */
export interface CreateDepositRefundResult {
  created: CreateDepositRefundItem[];
  skipped: { leaseAgreementId: number; reason: string }[];
}

/** body ของ POST /deposit-refund-request — ลูกค้ายื่นได้ทีละ 1 สัญญา */
export interface CreateDepositRefundRequestBody {
  leaseAgreementId: number;
  refund_method?: DepositRefundMethod;
  note?: string;
}

/**
 * สัญญากลางของทั้งของจริงและ mock — ./index.ts assign ทั้งสอง class ให้ตัวแปร type นี้
 * ถ้า signature ฝั่งใดฝั่งหนึ่งเพี้ยน compile จะไม่ผ่านทันที (กัน mock drift จากของจริง)
 */
export interface IDepositRefundApi {
  findAll(
    side: DepositRefundSide,
    params: DepositRefundParams
  ): Promise<AxiosResponse<BaseResponse<Paginated<DepositRefundRow>>>>;
  summary(
    side: DepositRefundSide,
    params: Omit<DepositRefundParams, "status" | "page" | "limit">
  ): Promise<AxiosResponse<BaseResponse<DepositRefundSummary>>>;
  filterZone(
    side: DepositRefundSide,
    branchId?: number
  ): Promise<AxiosResponse<BaseResponse<DepositRefundZoneOption[]>>>;
  filterStall(
    side: DepositRefundSide,
    branchId?: number
  ): Promise<AxiosResponse<BaseResponse<DepositRefundStallOption[]>>>;
  findById(
    side: DepositRefundSide,
    id: number
  ): Promise<AxiosResponse<BaseResponse<DepositRefundDetail>>>;
  log(
    side: DepositRefundSide,
    id: number
  ): Promise<AxiosResponse<BaseResponse<DepositRefundLog[]>>>;
  fileAll(
    side: DepositRefundSide,
    id: number
  ): Promise<AxiosResponse<BaseResponse<DepositRefundFile[]>>>;
  receipt(
    params: DepositRefundReceiptParams
  ): Promise<AxiosResponse<BaseResponse<Paginated<DepositRefundReceiptRow>>>>;
  deductDefault(
    id: number
  ): Promise<AxiosResponse<BaseResponse<DepositRefundDeductDefault>>>;
  ownerSearch(params: {
    keyword?: string;
    branchId?: number;
    only_has_deposit_lease?: 0 | 1;
    page?: number;
    limit?: number;
  }): Promise<AxiosResponse<BaseResponse<Paginated<DepositRefundOwner>>>>;
  ownerLease(
    userId: number,
    branchId?: number
  ): Promise<AxiosResponse<BaseResponse<DepositRefundLease[]>>>;
  myLease(): Promise<AxiosResponse<BaseResponse<DepositRefundLease[]>>>;
  create(
    body: CreateDepositRefundBody
  ): Promise<AxiosResponse<BaseResponse<CreateDepositRefundResult>>>;
  createRequest(
    body: CreateDepositRefundRequestBody
  ): Promise<AxiosResponse<BaseResponse<CreateDepositRefundItem>>>;
  review(id: number, body: FormData): Promise<AxiosResponse<BaseResponse<any>>>;
  remove(id: number): Promise<AxiosResponse<BaseResponse<boolean>>>;
  cancel(id: number): Promise<AxiosResponse<BaseResponse<boolean>>>;
  downloadFile(downloadUrl: string): Promise<AxiosResponse<Blob>>;
}

/**
 * axios 0.22 ประกาศ AxiosResponse<T = never> ถ้าไม่ใส่ generic res.data จะเป็น never
 * ทุกเมธอดจึงต้องระบุ type ของ data ให้ครบ
 */
export default class DepositRefundApi extends BaseAPI {
  static findAll(
    side: DepositRefundSide,
    params: DepositRefundParams
  ): Promise<AxiosResponse<BaseResponse<Paginated<DepositRefundRow>>>> {
    return this.api.get(BASE_PATH[side], { params: { ...params } });
  }

  static summary(
    side: DepositRefundSide,
    params: Omit<DepositRefundParams, "status" | "page" | "limit">
  ): Promise<AxiosResponse<BaseResponse<DepositRefundSummary>>> {
    return this.api.get(`${BASE_PATH[side]}/summary`, { params: { ...params } });
  }

  static filterZone(
    side: DepositRefundSide,
    branchId?: number
  ): Promise<AxiosResponse<BaseResponse<DepositRefundZoneOption[]>>> {
    return this.api.get(`${BASE_PATH[side]}/filter/zone`, { params: { branchId } });
  }

  static filterStall(
    side: DepositRefundSide,
    branchId?: number
  ): Promise<AxiosResponse<BaseResponse<DepositRefundStallOption[]>>> {
    return this.api.get(`${BASE_PATH[side]}/filter/stall`, { params: { branchId } });
  }

  static findById(
    side: DepositRefundSide,
    id: number
  ): Promise<AxiosResponse<BaseResponse<DepositRefundDetail>>> {
    return this.api.get(`${BASE_PATH[side]}/${id}`);
  }

  static log(
    side: DepositRefundSide,
    id: number
  ): Promise<AxiosResponse<BaseResponse<DepositRefundLog[]>>> {
    return this.api.get(`${BASE_PATH[side]}/${id}/log`);
  }

  static fileAll(
    side: DepositRefundSide,
    id: number
  ): Promise<AxiosResponse<BaseResponse<DepositRefundFile[]>>> {
    return this.api.get(`${BASE_PATH[side]}/${id}/file`);
  }

  /**
   * ตารางใบเสร็จรับเงิน — ฝั่งเจ้าหน้าที่เท่านั้น (role CUSTOMER ยิงมาได้ 403)
   * ไฟล์ PDF ไม่ได้อยู่ในเส้นนี้ ต้องเอา download_url ของแต่ละแถวไปยิงต่อ
   * (ดู downloadDepositRefundReceipt ใน depositRefundFile.utils.ts)
   */
  static receipt(
    params: DepositRefundReceiptParams
  ): Promise<AxiosResponse<BaseResponse<Paginated<DepositRefundReceiptRow>>>> {
    return this.api.get(`${BASE_PATH.ADMIN}/receipt`, { params: { ...params } });
  }

  /**
   * ยอดหักตั้งต้นจากคำสั่งเรียกคืนแผงค้าของสัญญาใบเดียวกัน — เอาไป prefill ฟอร์มพิจารณา
   *
   * ฝั่งเจ้าหน้าที่เท่านั้น (ลูกค้าได้ 403) จึงผูก BASE_PATH.ADMIN ตายตัว ไม่รับ side
   * ไม่มี query param และไม่เขียน DB เรียกซ้ำได้ไม่มีผลข้างเคียง
   *
   * ระวัง: ค่าที่ได้คือ "ค่าตั้งต้นที่ควรใช้" ไม่ใช่ยอดที่บันทึกจริง
   * ยอดที่บันทึกจริงของคำร้องที่พิจารณาแล้วอยู่ใน deducts[] ของ findById()
   */
  static deductDefault(
    id: number
  ): Promise<AxiosResponse<BaseResponse<DepositRefundDeductDefault>>> {
    return this.api.get(`${BASE_PATH.ADMIN}/${id}/deduct-default`);
  }

  /** ฝั่งเจ้าหน้าที่เท่านั้น — ช่อง "ผู้ขอคืนค่ามัดจำ" */
  static ownerSearch(params: {
    keyword?: string;
    branchId?: number;
    /** default 1 = เฉพาะคนที่มีสัญญาที่มีเงินมัดจำ */
    only_has_deposit_lease?: 0 | 1;
    page?: number;
    limit?: number;
  }): Promise<AxiosResponse<BaseResponse<Paginated<DepositRefundOwner>>>> {
    return this.api.get(`${BASE_PATH.ADMIN}/owner/search`, { params: { ...params } });
  }

  /** ฝั่งเจ้าหน้าที่ — สัญญาที่มีเงินมัดจำของผู้เช่าที่เลือก */
  static ownerLease(
    userId: number,
    branchId?: number
  ): Promise<AxiosResponse<BaseResponse<DepositRefundLease[]>>> {
    return this.api.get(`${BASE_PATH.ADMIN}/owner/${userId}/lease`, {
      params: { branchId },
    });
  }

  /** ฝั่งลูกค้า — สัญญาของตัวเอง ไม่ต้องส่ง userId (scope จาก token) */
  static myLease(): Promise<AxiosResponse<BaseResponse<DepositRefundLease[]>>> {
    return this.api.get(`${BASE_PATH.CUSTOMER}/lease`);
  }

  /** ฝั่งเจ้าหน้าที่ยื่นแทนลูกค้า — ได้หลายสัญญา ตอบ 201 พร้อม created[]/skipped[] */
  static create(
    body: CreateDepositRefundBody
  ): Promise<AxiosResponse<BaseResponse<CreateDepositRefundResult>>> {
    return this.api.post(BASE_PATH.ADMIN, body);
  }

  /** ฝั่งลูกค้ายื่นเอง — ทีละ 1 สัญญา */
  static createRequest(
    body: CreateDepositRefundRequestBody
  ): Promise<AxiosResponse<BaseResponse<CreateDepositRefundItem>>> {
    return this.api.post(BASE_PATH.CUSTOMER, body);
  }

  /**
   * multipart ยิงตรง ห้าม set Content-Type เอง ต้องให้ browser ใส่ boundary
   * body: result, note?, deduct_move_out?, deduct_damage?, refund_method?, refund_date?,
   *       doc_ref?, files[]
   * refund_method ส่งมา = เขียนทับ, ไม่ส่ง = คงค่าเดิมตอนยื่น (ไม่ล้างเป็น null)
   */
  static review(id: number, body: FormData): Promise<AxiosResponse<BaseResponse<any>>> {
    return this.api.post(`${BASE_PATH.ADMIN}/${id}/review`, body);
  }

  /** ฝั่งเจ้าหน้าที่ลบคำร้อง (soft delete) ได้เฉพาะ WAITING */
  static remove(id: number): Promise<AxiosResponse<BaseResponse<boolean>>> {
    return this.api.delete(`${BASE_PATH.ADMIN}/${id}`);
  }

  /** ฝั่งลูกค้ายกเลิกคำร้องของตัวเอง ได้เฉพาะ WAITING -> สถานะเป็น CANCELED */
  static cancel(id: number): Promise<AxiosResponse<BaseResponse<boolean>>> {
    return this.api.patch(`${BASE_PATH.CUSTOMER}/${id}/cancel`);
  }

  /**
   * ดาวน์โหลดไฟล์แนบ — ผ่าน JWT guard จึงต้องยิงด้วย axios instance ไม่ใช่เปิด URL ตรง
   * responseType blob บังคับ ไม่งั้น axios parse เป็น string แล้วไฟล์ binary เสีย
   */
  static downloadFile(downloadUrl: string): Promise<AxiosResponse<Blob>> {
    return this.api.get(downloadUrl, { responseType: "blob" });
  }
}
